/* Warehouse Bold — shared drawer mechanics.
 *
 * Any <dialog class="wb-drawer" id="X"> opens from any
 * [data-wb-drawer="X"] trigger. Native <dialog> so the backdrop, Escape,
 * the focus trap and the inert background are the platform's job.
 *
 * ⚠ The slide-in class is set after a forced reflow, NOT in a
 * requestAnimationFrame callback. rAF is not guaranteed to be prompt, and a
 * throttled renderer left an earlier drawer parked off-screen at
 * translateX(100%) indefinitely. Reading offsetWidth commits the start state
 * so the transition still animates.
 *
 * ⚠ Closing waits for transitionend with a timeout backstop: a transition
 * that never fires must not strand an open dialog.
 */
(() => {
  /* ⚠ Idempotent. These are DELEGATED document listeners, so a second
     execution would double-bind them and every trigger would open-then-close.
     The file is loaded once from layout/theme.liquid now that the cart
     drawer's dialog needs it site-wide, but a section that re-adds the tag
     would otherwise break every drawer on the page silently. */
  if (window.__wbDrawerBound) return;
  window.__wbDrawerBound = true;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Two presentations, one set of mechanics: .wb-drawer slides from the
     right, .wb-modal is centred. Both open/close identically. */
  const isOurs = (el) => !!(el && el.classList && (el.classList.contains('wb-drawer') || el.classList.contains('wb-modal')));

  /* iOS 26 samples its chrome tint from the page once a dialog covers it,
     falling back to the body background; wb-header.css turns the body navy
     while this class is present. Counted, not toggled blindly — two dialogs
     can never be open at once today, but a count cannot rot if that changes. */
  let topLayerCount = 0;
  const tintOn = () => { topLayerCount++; document.documentElement.classList.add('wb-top-layer-open'); };
  const tintOff = () => {
    topLayerCount = Math.max(0, topLayerCount - 1);
    if (!topLayerCount) document.documentElement.classList.remove('wb-top-layer-open');
  };

  const open = (dlg) => {
    if (!dlg || typeof dlg.showModal !== 'function') return;
    if (dlg.open) {
      /* ⚠ Reopened MID-EXIT. close() defers the real dlg.close() behind
         transitionend/timeout, so for up to 400ms a "closed" dialog is still
         technically open — and a click in that window used to be swallowed
         here while the pending timer then closed (and tore down) the view the
         shopper had just reopened. Revive instead: cancel the pending close
         and play the entrance again. Found 2026-08-17 via the review photo
         viewer; applies to every drawer, the cart included. */
      if (dlg.__wbClosing) {
        dlg.__wbClosing();
        dlg.__wbClosing = null;
        tintOn();
        void dlg.offsetWidth;
        dlg.classList.add('is-in');
      }
      return;
    }
    dlg.showModal();
    /* ⚠ Move focus off the ✕. showModal() focuses the first focusable
       descendant, which is the close button, and iOS Safari counts that
       programmatic focus as :focus-visible — so every open painted a navy ring
       around the round ✕ (Owen's recording, 2026-08-09). Focusing the dialog
       itself is the standard modal pattern and reads better anyway: a screen
       reader announces the dialog and its label rather than "close, button".
       The ring still appears for a real Tab, which is the point of it. */
    if (!dlg.hasAttribute('tabindex')) dlg.setAttribute('tabindex', '-1');
    try { dlg.focus({ preventScroll: true }); } catch (e) { /* older Safari */ }
    tintOn();
    void dlg.offsetWidth;
    dlg.classList.add('is-in');
  };

  const close = (dlg) => {
    if (!dlg || !dlg.open) return;
    if (dlg.__wbClosing) return; // already on its way out — don't double-run the exit
    tintOff();
    dlg.classList.remove('is-in');
    // ⚠ Reduced motion takes the same exit, or it skips the wb:closed signal
    // below and consumers silently never tear down for those users.
    if (reduced()) {
      dlg.close();
      dlg.dispatchEvent(new CustomEvent('wb:closed', { bubbles: false }));
      return;
    }
    let settled = false;
    /* ⚠ Filter on target. transitionend BUBBLES, so any animated descendant
       ends the exit early — the review photo viewer fades its <img> in on load
       (0.18s) and that alone satisfied a one-shot listener, closing the dialog
       mid-slide. No existing drawer animates anything inside itself, so this is
       a no-op for them and a fix for the first overlay that does. */
    const finish = (e) => {
      if (e && e.target !== dlg) return;
      if (settled) return;
      settled = true;
      dlg.__wbClosing = null;
      dlg.removeEventListener('transitionend', finish);
      dlg.close();
      /* ⚠ An explicit signal, not the UA's own `close` event. Chromium has been
         observed here not firing `close` at all (verified 2026-08-16 against a
         native, unpatched close()), so anything that MUST run after a dismissal
         — teardown, restoring focus to whatever opened the dialog — cannot be
         hung on it. Fires after close() so listeners see the settled state. */
      dlg.dispatchEvent(new CustomEvent('wb:closed', { bubbles: false }));
    };
    dlg.addEventListener('transitionend', finish);
    const timer = setTimeout(finish, 400);
    /* The canceller open() calls if the dialog is reopened before the exit
       lands — the pending finish must never close a revived view. */
    dlg.__wbClosing = () => {
      settled = true;
      clearTimeout(timer);
      dlg.removeEventListener('transitionend', finish);
    };
  };

  // Delegated so triggers rendered later (section re-render in the theme
  // editor, or a drawer opened from another drawer) still work.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-wb-drawer]');
    if (trigger) {
      const dlg = document.getElementById(trigger.getAttribute('data-wb-drawer'));
      if (dlg) { e.preventDefault(); open(dlg); }
      return;
    }
    const closer = e.target.closest && e.target.closest('[data-wb-drawer-close]');
    if (closer) { e.preventDefault(); close(closer.closest('dialog')); return; }
    // Backdrop: a click on the dialog element ITSELF is the backdrop; a click
    // on anything inside it bubbles from a descendant instead.
    if (e.target.tagName === 'DIALOG' && isOurs(e.target)) close(e.target);
  });

  // Escape: intercept the UA's instant close so the drawer slides out.
  document.addEventListener('cancel', (e) => {
    const dlg = e.target;
    if (isOurs(dlg)) {
      e.preventDefault();
      close(dlg);
    }
  }, true);

  /* ⚠ The programmatic entry point, for dialogs whose OPEN is conditional and
     so cannot be a plain [data-wb-drawer] trigger. Ask a question is the case:
     it opens only once Judge.me's bundle is present, and its link has to stay a
     working fallback to the contact page when that bundle is content-blocked —
     an attribute trigger would open the drawer unconditionally and turn the
     fallback into a dead end.

     ⚠ Use this rather than calling showModal() directly. A dialog that carries
     .wb-drawer/.wb-modal already gets this file's ESC and backdrop handling
     whether it asked for it or not, so opening by hand gives you HALF the
     contract: the close half runs twice (once here, once in your own code) and
     the open half — the iOS 26 chrome tint, the focus-off-the-✕ fix — never
     runs at all. That is exactly what the ask drawer did until 2026-08-17. */
  window.WBDrawer = { open: open, close: close };
})();
